import { useTranslation } from '@frontend/i18n';
import { Icon, IconName } from '@frontend/icons';
import { InboxPrefixes } from '@frontend/tracking-prefixes';
import { theme } from '@frontend/theme';
import { Button, PopoverMenu, PopoverMenuItem, Text } from '@frontend/design-system';
import { FilterItemPopoverCheckboxField } from '../filter-item-popover-checkbox-field';
import { GetItemPropsFn, GetMenuPropsFn } from '../types';

type AiReceptionistFilterPopoverMenuProps = {
  getMenuProps: GetMenuPropsFn;
  getItemProps: GetItemPropsFn;
  groupIds?: string[];
  toggleItems: (selectedAiStatuses: string[]) => void;
  selectedAiStatuses: string[];
};

export const AiReceptionistFilterPopoverMenu = ({
  getMenuProps,
  getItemProps,
  toggleItems,
  selectedAiStatuses,
}: AiReceptionistFilterPopoverMenuProps): ReturnType<typeof PopoverMenu> => {
  const { t } = useTranslation('inbox');

  // TODO: waiting for other statuses: 'waiting for response'
  const availableAiStatuses = ['isActive', 'isInactive', 'needsAttention', 'newAppointment', 'complete'];

  const statusLabelMap: Record<string, string> = {
    isActive: t('AI Receptionist Active'),
    isInactive: t('AI Receptionist Not Active'),
    needsAttention: t('Needs Attention'),
    newAppointment: t('New Appointment'),
    complete: t('Complete'),
  };

  const statusIconMap: Record<string, IconName> = {
    isActive: 'aiweave-assistant',
    needsAttention: 'aineeds-attention',
    newAppointment: 'calendar',
    complete: 'check',
  };

  const hasAllSelected = availableAiStatuses.every((status) => selectedAiStatuses.includes(status));
  const hasNoneSelected = selectedAiStatuses.length === 0;

  const handleSelectAll = () => {
    const unselectedStatuses = availableAiStatuses.filter((status) => !selectedAiStatuses.includes(status));
    toggleItems(unselectedStatuses);
  };

  const handleClear = () => {
    toggleItems(selectedAiStatuses);
  };

  const handleToggleActiveStatus = (aiStatus: string) => {
    if (aiStatus === 'isInactive' && selectedAiStatuses.includes('isActive')) {
      toggleItems(['isActive']);
    }
    if (aiStatus === 'isActive' && selectedAiStatuses.includes('isInactive')) {
      toggleItems(['isInactive']);
    }
  };

  return (
    <PopoverMenu {...getMenuProps()} css={{ minWidth: 180 }}>
      <PopoverMenuItem
        as='span'
        {...getItemProps({
          index: 0,
          disableCloseOnSelect: true,
        })}
        css={{
          marginTop: theme.spacing(1),
          padding: theme.spacing(1, 0.75),
          borderBottom: `1px solid ${theme.colors.neutral20}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          ':hover': {
            backgroundColor: 'inherit',
          },
          ':focus': {
            backgroundColor: 'inherit',
          },
        }}
      >
        <Button
          variant='tertiary'
          css={{
            color: theme.font.colors.primary,
            fontWeight: theme.font.weight.semibold,
          }}
          onClick={handleSelectAll}
          disabled={hasAllSelected}
          trackingId={`${InboxPrefixes.List}-filters-popover-ai-receptionist-select-all`}
        >
          {t('Select All')}
        </Button>
        <Button
          variant='tertiary'
          css={{
            color: theme.font.colors.primary,
            fontWeight: theme.font.weight.semibold,
          }}
          onClick={handleClear}
          disabled={hasNoneSelected}
          trackingId={`${InboxPrefixes.List}-filters-popover-ai-receptionist-clear`}
        >
          {t('Clear')}
        </Button>
      </PopoverMenuItem>
      <div
        css={{
          maxHeight: 240,
          overflowY: 'auto',
        }}
      >
        {availableAiStatuses.map((aiStatus, index) => (
          <PopoverMenuItem
            key={aiStatus}
            {...getItemProps({
              index: index + 1,
              disableCloseOnSelect: true,
              onClickCapture: (e) => {
                e.preventDefault();
                e.stopPropagation();
                handleToggleActiveStatus(aiStatus);
                toggleItems([aiStatus]);
              },
            })}
            css={{
              width: '100%',
              cursor: 'pointer',
              '*': {
                cursor: 'pointer',
              },
            }}
          >
            <FilterItemPopoverCheckboxField
              isChecked={selectedAiStatuses.includes(aiStatus)}
              name={`ai-receptionist-${aiStatus}`}
              label={
                <div
                  css={{
                    display: 'flex',
                    alignItems: 'center',
                    columnGap: theme.spacing(1),
                  }}
                >
                  {aiStatus !== 'isInactive' && <Icon name={statusIconMap[aiStatus]} size={16} />}
                  <Text>{statusLabelMap[aiStatus]}</Text>
                </div>
              }
            />
          </PopoverMenuItem>
        ))}
      </div>
    </PopoverMenu>
  );
};
